/**
 * useTest Hook
 * Manages test state, question loading, and answer submission
 */
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  getNextQuestion, 
  submitAnswer, 
  completeTest, 
  getSectionInstruction, 
  navigateQuestion, 
  savePreRuleTime, 
  markInstructionShown, 
  saveDistractionData 
} from '../services/api';
import { Question, SectionInstruction } from '../types';

interface UseTestReturn {
  // State
  question: Question | null;
  sectionInstruction: SectionInstruction | null;
  selectedAnswer: string | null;
  loading: boolean;
  currentSection: string | null;
  version: string;
  showMemorization: boolean;
  showDistraction: boolean;
  showPreRule: boolean;
  dimensionTimeRemaining: number | null;
  canGoBack: boolean;
  
  // Actions
  setSelectedAnswer: (answer: string | null) => void;
  handleStartSection: () => Promise<void>;
  handleMemorizationComplete: () => void;
  handleDistractionComplete: (timeSpent: number, moves: number) => Promise<void>;
  handlePreRuleComplete: () => Promise<void>;
  handleSubmit: () => Promise<void>;
  handleQuit: () => Promise<void>;
  handleNavigate: (direction: 'back' | 'forward') => Promise<void>;
  loadNextQuestion: () => Promise<void>;
}

export const useTest = (): UseTestReturn => {
  // Core state
  const [question, setQuestion] = useState<Question | null>(null);
  const [sectionInstruction, setSectionInstruction] = useState<SectionInstruction | null>(null);
  const [selectedAnswer, setSelectedAnswer] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [currentSection, setCurrentSection] = useState<string | null>(null);
  const [version, setVersion] = useState<string>('A');
  
  // Phase visibility
  const [showMemorization, setShowMemorization] = useState(false);
  const [showDistraction, setShowDistraction] = useState(false);
  const [showPreRule, setShowPreRule] = useState(false);
  
  // Dimension state
  const [dimensionTimeRemaining, setDimensionTimeRemaining] = useState<number | null>(null);
  const [canGoBack, setCanGoBack] = useState(false);
  
  // Time tracking
  const [instructionStartTime, setInstructionStartTime] = useState<number | null>(null);
  const [preRuleStartTime, setPreRuleStartTime] = useState<number | null>(null);
  const [questionStartTime, setQuestionStartTime] = useState<number | null>(null);
  
  const navigate = useNavigate();
  const sessionId = localStorage.getItem('sessionId');

  // Load next question
  const loadNextQuestion = useCallback(async () => {
    if (!sessionId) return;
    
    setLoading(true);
    try {
      const response = await getNextQuestion(sessionId);
      const nextQuestion = response.data;
      
      if (nextQuestion.version) {
        setVersion(nextQuestion.version);
      }

      setDimensionTimeRemaining(nextQuestion.dimension_time_remaining);
      setCanGoBack(nextQuestion.can_go_back);

      // Check if new section
      if (nextQuestion.section_id && nextQuestion.section_id !== currentSection) {
        const instrResponse = await getSectionInstruction(
          nextQuestion.version || version, 
          nextQuestion.section_id
        );
        setSectionInstruction(instrResponse.data);
        setCurrentSection(nextQuestion.section_id);
        setInstructionStartTime(Date.now());
      } else {
        setSectionInstruction(null);
        
        if (nextQuestion.pre_question_rule) {
          setShowPreRule(true);
          setPreRuleStartTime(Date.now());
        } else if (nextQuestion.phases?.memorization) {
          setShowMemorization(true);
        } else {
          setQuestionStartTime(Date.now());
        }
        
        setQuestion(nextQuestion);
      }
    } catch (err: any) {
      if (err.response?.status === 404) {
        const attemptId = localStorage.getItem('attemptId');
        navigate(`/results/${attemptId}`);
      }
    } finally {
      setLoading(false);
    }
  }, [sessionId, currentSection, version, navigate]);

  // Initial load
  useEffect(() => {
    if (!sessionId) {
      navigate('/dashboard');
      return;
    }
    loadNextQuestion();
  }, []);

  // Start section handler
  const handleStartSection = useCallback(async () => {
    const timeSpent = instructionStartTime 
      ? Math.floor((Date.now() - instructionStartTime) / 1000)
      : 0;
    
    if (currentSection && sessionId) {
      try {
        await markInstructionShown(sessionId, currentSection, timeSpent);
      } catch (err) {
        console.error('Failed to save instruction time:', err);
      }
    }
    
    setSectionInstruction(null);
    setInstructionStartTime(null);
    setLoading(true);
    
    const res = await getNextQuestion(sessionId!);
    const nextQuestion = res.data;
    
    if (nextQuestion.pre_question_rule) {
      setShowPreRule(true);
      setPreRuleStartTime(Date.now());
    } else if (nextQuestion.phases?.memorization) {
      setShowMemorization(true);
    } else {
      setQuestionStartTime(Date.now());
    }
    
    setQuestion(nextQuestion);
    setLoading(false);
  }, [sessionId, currentSection, instructionStartTime]);

  // Memorization complete handler
  const handleMemorizationComplete = useCallback(() => {
    if (question?.phases?.distraction) {
      setShowMemorization(false);
      setShowDistraction(true);
    } else {
      setShowMemorization(false);
      setQuestionStartTime(Date.now());
    }
  }, [question]);

  // Distraction complete handler
  const handleDistractionComplete = useCallback(async (timeSpent: number, moves: number) => {
    if (question && sessionId) {
      try {
        await saveDistractionData(sessionId, question.question_id, timeSpent, moves);
      } catch (err) {
        console.error('Failed to save distraction data:', err);
      }
    }
    
    setShowDistraction(false);
    setQuestionStartTime(Date.now());
  }, [question, sessionId]);

  // Pre-rule complete handler
  const handlePreRuleComplete = useCallback(async () => {
    if (preRuleStartTime && question && sessionId) {
      const timeSpent = Math.floor((Date.now() - preRuleStartTime) / 1000);
      try {
        await savePreRuleTime(sessionId, question.question_id, timeSpent);
      } catch (err) {
        console.error('Failed to save pre-rule time:', err);
      }
    }
    
    setShowPreRule(false);
    
    if (question?.phases?.memorization) {
      setShowMemorization(true);
    } else {
      setQuestionStartTime(Date.now());
    }
  }, [preRuleStartTime, question, sessionId]);

  // Submit answer handler
  const handleSubmit = useCallback(async () => {
    if (!question || !sessionId) return;
    
    const timeSpent = questionStartTime 
      ? Math.floor((Date.now() - questionStartTime) / 1000)
      : 0;
    
    setLoading(true);
    try {
      await submitAnswer(sessionId, question.question_id, selectedAnswer || '', timeSpent);
      setSelectedAnswer(null);
      setShowPreRule(false);
      setShowMemorization(false);
      setShowDistraction(false);
      setQuestionStartTime(null);
      await loadNextQuestion();
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  }, [question, sessionId, selectedAnswer, questionStartTime, loadNextQuestion]);

  // Quit handler
  const handleQuit = useCallback(async () => {
    if (!sessionId) return;
    
    if (window.confirm('Etes-vous sur de vouloir quitter? Votre progression sera sauvegardee.')) {
      await completeTest(sessionId);
      const attemptId = localStorage.getItem('attemptId');
      navigate(`/results/${attemptId}`);
    }
  }, [sessionId, navigate]);

  // Navigate handler
  const handleNavigate = useCallback(async (direction: 'back' | 'forward') => {
    if (!sessionId) return;
    
    setLoading(true);
    try {
      const response = await navigateQuestion(sessionId, direction);
      const data = response.data;
      setQuestion(data);
      setDimensionTimeRemaining(data.dimension_time_remaining);
      setCanGoBack(data.can_go_back);
      setSelectedAnswer(null);
      setQuestionStartTime(Date.now());
    } catch (err) {
      console.error('Navigation error:', err);
    } finally {
      setLoading(false);
    }
  }, [sessionId]);

  return {
    question,
    sectionInstruction,
    selectedAnswer,
    loading,
    currentSection,
    version,
    showMemorization,
    showDistraction,
    showPreRule,
    dimensionTimeRemaining,
    canGoBack,
    setSelectedAnswer,
    handleStartSection,
    handleMemorizationComplete,
    handleDistractionComplete,
    handlePreRuleComplete,
    handleSubmit,
    handleQuit,
    handleNavigate,
    loadNextQuestion
  };
};
